/*
 * WP-10 portal API client (§14.2). Thin fetch wrapper:
 * - same-origin credentials (session cookie)
 * - CSRF pre-session token via X-Portal-CSRF header
 * - PORTAL_* error-code mapping
 * - Idempotency-Key support for submits / step-up retries
 */

export const API_BASE = '/api/portal/v1';

export class PortalApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, code: string, details?: unknown) {
    super(code);
    this.name = 'PortalApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function isPortalApiError(err: unknown, code?: string): err is PortalApiError {
  return err instanceof PortalApiError && (code === undefined || err.code === code);
}

type Json = Record<string, unknown> | unknown[] | string | number | boolean | null;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: Json;
  idempotencyKey?: string;
  /** Binary download — returns a Response instead of parsed JSON. */
  raw?: boolean;
  /* Synchronous gate run after CSRF resolution, immediately before fetch —
   * the last application-level check before the request leaves. Throwing
   * aborts the dispatch (F1.3 owner/dispatch binding). */
  beforeSend?: () => void;
}

let csrfToken: string | null = null;
let csrfPromise: Promise<string> | null = null;

/** Fetches (and caches) the pre-session CSRF token from GET /auth/csrf. */
export async function ensureCsrfToken(): Promise<string> {
  if (csrfToken) return csrfToken;
  csrfPromise ??= fetch(`${API_BASE}/auth/csrf`, { credentials: 'same-origin' })
    .then(async (res) => {
      if (!res.ok) throw new PortalApiError(res.status, 'PORTAL_CSRF_UNAVAILABLE');
      const data = (await res.json()) as { csrfToken?: string };
      csrfToken = data.csrfToken ?? '';
      return csrfToken;
    })
    .finally(() => {
      csrfPromise = null;
    });
  return csrfPromise;
}

export function clearCsrfToken(): void {
  csrfToken = null;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, raw } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    try {
      headers['x-hp-csrf'] = await ensureCsrfToken();
    } catch {
      // CSRF fetch failed — let the request surface the real error.
    }
  }
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  /* The CSRF await above is itself an async boundary — callers that captured
   * an owner/intent context re-verify it here, synchronously, so nothing can
   * leave under a stale continuation (F1.3). */
  options.beforeSend?.();

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'same-origin',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (raw) {
    if (!res.ok) throw await toError(res);
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }

  if (!res.ok) {
    const code =
      (data as { code?: string; error?: { code?: string } } | undefined)?.code ??
      (data as { error?: { code?: string } } | undefined)?.error?.code ??
      `PORTAL_HTTP_${res.status}`;
    const details =
      (data as { details?: unknown; error?: { details?: unknown } } | undefined)?.details ??
      (data as { error?: { details?: unknown } } | undefined)?.error?.details;
    throw new PortalApiError(res.status, code, details);
  }
  return data as T;
}

async function toError(res: Response): Promise<PortalApiError> {
  let code = `PORTAL_HTTP_${res.status}`;
  let details: unknown;
  try {
    const data = (await res.json()) as {
      code?: string;
      details?: unknown;
      error?: { code?: string; details?: unknown };
    };
    code = data.code ?? data.error?.code ?? code;
    details = data.details ?? data.error?.details;
  } catch {
    /* non-JSON error body */
  }
  return new PortalApiError(res.status, code, details);
}

export const portalApi = {
  /** Same-origin URL for binary downloads (statements, contract PDFs). */
  url: (path: string) => `${API_BASE}${path}`,
  get: <T>(path: string) => request<T>(path),
  post: <T>(
    path: string,
    body?: Json,
    idempotencyKey?: string,
    opts?: { beforeSend?: () => void },
  ) => request<T>(path, { method: 'POST', body, idempotencyKey, beforeSend: opts?.beforeSend }),
  patch: <T>(path: string, body: Json) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  getRaw: (path: string) => request<Response>(path, { raw: true }),
};

/** Small helper for idempotency keys (F4 submit, step-up retry). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
